import {
  Component,
  Input,
  OnInit,
  EventEmitter, Output, HostListener, ViewChild, OnDestroy, ElementRef, OnChanges, SimpleChanges, ViewEncapsulation
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { debounceTime, tap } from "rxjs/operators";
import { ISearchInput } from "./search-widget.interface";
import { ProgressComponent } from "@bssh/comp-lib";
import { SubSink } from "subsink";
import { Observable } from "rxjs";

export enum KEY_CODE {
  UP_ARROW = 38,
  DOWN_ARROW = 40,
  ENTER = 13,
  ESCAPE = 27 
}

@Component({
  selector: "app-search-widget",
  templateUrl: "./search-widget.component.html",
  styleUrls: ["./search-widget.component.scss"],
  encapsulation: ViewEncapsulation.None
})
export class SearchWidgetComponent implements OnInit, OnDestroy, OnChanges {
  private subs = new SubSink();
  public search = new FormControl("");
  public isRequestSent = false;
  public isResultsVisible = false;
  public searchResults: ISearchInput[];
  public selectedSearchResultIndex: number;
  public previouslySelectedSearchResultIndex: number;
  public hoverItemSelected: boolean;

  @Input() public searchAreas: ISearchInput[];
  @Input() public width: string;
  @Input() public overlayTop: string;
  @Input() public isActive: string;
  @Input() public closeOnSelect: string;

  @Input() public resultsMapper: (initData: ISearchInput, searchString?: string) => ISearchInput;
  @Input() public requester: (searchString: string) => Observable<any>;
  // tslint:disable-next-line: no-output-on-prefix
  @Output() public onHide: EventEmitter<string> = new EventEmitter();
  // tslint:disable-next-line: no-output-on-prefix
  @Output() public onSelect: EventEmitter<any> = new EventEmitter();

  @ViewChild("search_widget_spinner", { static: true }) spinner: ProgressComponent;
  @ViewChild("searchBar", {static: true}) searchBarRef: ElementRef;

  private isStaticDataSet: boolean;

  @HostListener("keydown", ["$event"])
  onKeyDown(evt: KeyboardEvent): void {
    switch(evt.keyCode){
      case KEY_CODE.ESCAPE:
        this.closeWidget();
        break;
      case KEY_CODE.ENTER:
        if (this.search.value === null || this.searchResults === null || this.searchResults.length === 0){
          break;
        }
        if (this.selectedSearchResultIndex === null){
          this.selectedSearchResultIndex = 0;
        }
        this.selectChoice(this.searchResults[this.selectedSearchResultIndex]);
        break;
      case KEY_CODE.UP_ARROW:
        if (this.selectedSearchResultIndex !== null && this.selectedSearchResultIndex > 0){
          this.selectedSearchResultIndex--;
        }
        break;
      case KEY_CODE.DOWN_ARROW:
        if (this.selectedSearchResultIndex === null){
          this.selectedSearchResultIndex = 0;
        } else if (this.selectedSearchResultIndex < this.searchResults.length-1){
          this.selectedSearchResultIndex++;
        }
        break;
    }
  }

  constructor() {
  }

  public closeWidget(): void {
    this.isActive = "";
    this.hideResults();
    this.resetSelection();
    this.onHide.emit("com.search.hide");
  }

  public selectChoice(selectedData): void {
    this.onSelect.emit(selectedData);
    if (this.closeOnSelect) {
      this.closeWidget();
    }
  }

  private hideResults(): boolean {
    this.searchResults.length = 0;
    this.isResultsVisible = false;
    this.spinner.complete();
    return true;
  }

  ngOnInit() {
    this.isStaticDataSet = !this.requester;

    if (!this.resultsMapper) {
      this.resultsMapper = (initData: ISearchInput) => {
        return {
          label: initData.label,
          value: this.search.value,
          action: initData.action
        };
      };
    }

    this.searchResults = [];
    this.resetSelection();

    this.onValueChanges();
  }

  ngOnChanges(changes: SimpleChanges){
    if(changes['isActive'] && (changes['isActive'].currentValue === "active" || changes['isActive'].currentValue === 'true')){
      this.setSearchFocus();
    }
  }

  public setSearchFocus(){
    setTimeout(() => this.searchBarRef.nativeElement.focus());
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  private onValueChanges(): void {
    this.subs.sink = this.search.valueChanges
      .pipe(
        tap(() => {
          this.hideResults();
        }),
        debounceTime(300))
      .subscribe(val => {
        this.getResults(val);
      });
  }

  public hoverStatus(isHoverOn: boolean, currentHoverItem: number) {
    this.hoverItemSelected = isHoverOn;

    if(isHoverOn){
      if (this.selectedSearchResultIndex !== null){
        this.previouslySelectedSearchResultIndex = this.selectedSearchResultIndex;
      }
      this.selectedSearchResultIndex = currentHoverItem;
    } else if(!isHoverOn){
      this.selectedSearchResultIndex = this.previouslySelectedSearchResultIndex;
    }
  }

  private getResults(searchString: string) {
    if (!searchString) {
      return;
    }

    this.resetSelection();
    this.isResultsVisible = true;
    if (this.isStaticDataSet) {
      this.searchResults = this.searchAreas.map((item) => {
        return this.resultsMapper(item);
      });
      return;
    }

    this.isRequestSent = true;

    this.subs.sink = this.requester(searchString).subscribe((data) => {
      this.searchResults = data.map((item) => {
        this.isRequestSent = false;
        return this.resultsMapper(item, searchString);
      });
    });
  }

  private resetSelection(){
    this.selectedSearchResultIndex = null;
    this.previouslySelectedSearchResultIndex = null;
    this.hoverItemSelected = false;
  }
}
